import { Avatar, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Fab, InputBase, Link, List, ListItem, ListItemIcon, ListItemText, ListSubheader, Paper, Typography } from "@material-ui/core";
import { Delete, Person } from "@material-ui/icons";
import firebase from "firebase/app";
import { useSnackbar } from "notistack";
import React, { useState } from "react";
import { useHistory } from "react-router";
import { Link as RouterLink } from 'react-router-dom'

import { Collections } from "Collections";
import { useAppUser } from "components/AppUserProvider";
import { FullscreenLoading } from "components/FullscreenLoading";
import { useOrg } from "components/OrgProvider";
import { useOrgUser } from "components/OrgUserProvider";
import { PageNavigation } from "components/PageNavigation";
import { FirebaseUtils } from "firebaseUtils/FirebaseUtils";
import { AnalyticsEvent } from "models/AnalyticsEvent";
import { AppUser } from "models/AppUser";
import { noEmailText } from "models/Email";
import { Nullable } from "models/Nullable";
import { OrgRoles } from "models/OrgRoles";
import { noOrgUserNameText, OrgUser } from "models/OrgUser";
import { Paths } from "Paths";
import { dateFormat } from "Util/date.utils";
import { isEmpty } from "Util/isEmpty";

type Props = {
    id?: string
}

type ModalProps = {
    children: React.ReactNode
    title: string
    titlePrefixSlot?: React.ReactNode
    onClose: () => void
    onCancel?: () => void
    noCancel?: boolean
    noAutoFocus?: boolean
    fullWidth?: boolean
    okButtonText?: React.ReactNode
}

export function WarningModal({ onClose, title, children, onCancel, noCancel, noAutoFocus, fullWidth, okButtonText, titlePrefixSlot }: ModalProps) {
    return (
        <Dialog
            open
            onClose={onCancel}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            fullWidth={fullWidth}
        >
            <DialogTitle id="alert-dialog-title">{titlePrefixSlot}{title}</DialogTitle>
            <DialogContent>
                {children}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary" autoFocus={!noAutoFocus}>{isEmpty(okButtonText) ? 'Ok' : okButtonText}</Button>
                {(onCancel && !noCancel) ? (<Button onClick={onCancel} color="secondary" autoFocus={!noAutoFocus}>Avbryt</Button>) : null}
            </DialogActions>
        </Dialog>
    )
}

type OrgUserRolePickerProps = {
    orgUser: OrgUser
    ownAppUser: Nullable<AppUser>
    ownOrgUser: Nullable<OrgUser>
    isOwnUser: boolean
    isOrgOwner: boolean
}

export function OrgUserRolePicker({ orgUser, ownAppUser, isOwnUser, isOrgOwner, ownOrgUser }: OrgUserRolePickerProps) {
    const [ownerRoleWarning, setOwnerRoleWarning] = useState('')
    const { enqueueSnackbar } = useSnackbar()

    return (
        <>
            {ownerRoleWarning ? (
                <WarningModal title="Kan ikke fjerne rolle" onClose={() => setOwnerRoleWarning('')}>
                    <Typography variant="subtitle1">{ownerRoleWarning}</Typography>
                </WarningModal>
            ) : null}
            <ListSubheader>Roller</ListSubheader>
            {(
                OrgRoles.FlatEnumList.map((role) => {
                    const isSelected = !!orgUser?.orgRoles.map[role];
                    const isOwnerRole = role === OrgRoles.FlatEnum.OWNER
                    //Is this role OWNER and are you trying to change it for your own user?
                    const isOwnOwnerRole = (isOwnUser && isOwnerRole)
                    const isOrgOwnersOwnerRole = (isOrgOwner && isOwnerRole)
                    const canSelect = !!ownAppUser && !!ownOrgUser && !!ownOrgUser?.orgRoles.map.OWNER
                    const newValue = !isSelected || firebase.firestore.FieldValue.delete()
                    function toggleSelected() {
                        if (isOrgOwnersOwnerRole && isOwnOwnerRole) {
                            setOwnerRoleWarning('Du er eieren av organisasjonen. Din Eier-rolle kan ikke fjernes')
                        } else if (isOrgOwnersOwnerRole) {
                            setOwnerRoleWarning('Dette er eieren av organisasjonen. Denne personens Eier-rolle kan ikke fjernes')
                        } else if (isOwnOwnerRole) {
                            setOwnerRoleWarning('Hvis du ikke lenger ønsker å være Eier, få en annen Eier til å fjerne statusen for deg')
                        } else if (canSelect && !isOwnOwnerRole) {
                            if (!ownAppUser) {
                                console.error('No ownAppUser state!')
                                return
                            }
                            if (!ownOrgUser) {
                                console.error('No ownOrgUser state!')
                                return
                            }
                            const analyticsRoleData = {
                                orgId: orgUser.org.id,
                                isOrgOwner,
                                isOwnUser,
                                orgUserId: orgUser.id,
                                appUserId: orgUser.userId,
                                ownOrgUserId: ownOrgUser.id,
                                ownAppUserId: ownOrgUser.userId,
                                role,
                            }
                            FirebaseUtils.updateDocument<OrgUser>(
                                true,
                                Collections.ORG_USERS,
                                orgUser.id,
                                ownAppUser,
                                orgUser,
                                !isSelected ?
                                    (isOwnUser ? AnalyticsEvent.ADDED_OWN_ORG_ROLE : AnalyticsEvent.ADDED_ORG_ROLE)
                                    : (isOwnUser ? AnalyticsEvent.REMOVED_OWN_ORG_ROLE : AnalyticsEvent.REMOVED_ORG_ROLE),
                                AnalyticsEvent.UPDATED_ORG_USER,
                                AnalyticsEvent.UPDATE_ORG_USER_ERROR,
                                enqueueSnackbar,
                                !isSelected ? 'Rolle lagt til' : 'Rolle fjernet',
                                {
                                    orgRoles: {
                                        map: {
                                            [role]: newValue,
                                        }
                                    }
                                },
                                () => (analyticsRoleData),
                            )
                        }
                    }

                    const checkId = "check-" + role.toLowerCase().replaceAll('_', '-')

                    return (
                        <ListItem
                            key={role}
                            //@ts-ignore
                            button={canSelect ? true : false}
                            onClick={canSelect ? toggleSelected : undefined}
                            divider
                        >
                            <ListItemIcon>
                                <Checkbox
                                    checked={isSelected}
                                    edge="start"
                                    tabIndex={-1}
                                    disableRipple
                                    disabled={!canSelect || isOwnOwnerRole}
                                    inputProps={{ 'aria-labelledby': checkId }}
                                />
                            </ListItemIcon>
                            <ListItemText
                                id={checkId}
                                primary={OrgRoles.Flat[role].name}
                                secondary={OrgRoles.Flat[role].description}
                            />
                        </ListItem>
                    )
                })
            )}
        </>
    )
}

type DeleteOrgUserModalProps = {
    orgUser: OrgUser
    ownAppUser: AppUser
    ownOrgUser: OrgUser
    close: () => void
    isOwnUser: boolean
    isOrgOwner: boolean
    redirectOnDelete?: boolean
}

export function DeleteOrgUserModal({
    orgUser,
    ownAppUser,
    ownOrgUser,
    close,
    isOwnUser,
    isOrgOwner,
    redirectOnDelete,
}: DeleteOrgUserModalProps) {
    const history = useHistory()
    const { enqueueSnackbar } = useSnackbar()
    return (
        <WarningModal
            title="Fjern bruker"
            onCancel={close}
            onClose={() => {
                if (orgUser && ownAppUser && ownOrgUser) {
                    FirebaseUtils.deleteDocument(
                        true,
                        Collections.ORG_USERS,
                        orgUser.id,
                        ownAppUser,
                        ownOrgUser,
                        AnalyticsEvent.DELETED_ORG_USER,
                        isOwnUser ? AnalyticsEvent.DELETED_OWN_ORG_USER : AnalyticsEvent.DELETED_ORG_USER,
                        AnalyticsEvent.DELETE_ORG_USER_ERROR,
                        enqueueSnackbar,
                        'Bruker fjernet fra organisasjonen',
                        () => ({
                            orgId: orgUser.org.id,
                            isOrgOwner,
                            isOwnUser,
                            orgUserId: orgUser?.id,
                            appUserId: orgUser?.userId,
                            ownOrgUserId: ownOrgUser?.id,
                            ownAppUserId: ownOrgUser?.userId,
                        }),
                    )
                    if (redirectOnDelete) {
                        history.goBack()
                    } else {
                        close()
                    }
                }
            }}>
            <Typography variant="subtitle1">Ønsker du å fjerne brukeren fra organisasjonen? Dette kan ikke angres</Typography>
        </WarningModal>
    )
}

export function OrgUserScreen({ id }: Props) {
    const [orgDoc, loadingOrg] = useOrg()
    const [ownOrgUserDoc, loadingOwnOrgUser] = useOrgUser()
    const [ownAppUserDoc, loadingOwnAppUser] = useAppUser()

    const [orgUserDoc, loadingOrgUser] = FirebaseUtils.useDocumentData<OrgUser>(
        true,
        Collections.ORG_USERS,
        id || null,
        AnalyticsEvent.FETCH_ORG_USER_ERROR
    )

    const org = orgDoc?.data || null
    const ownOrgUser = ownOrgUserDoc?.data || null
    const ownAppUser = ownAppUserDoc?.data || null
    const orgUser = orgUserDoc?.data || null

    const [showDeleteUser, setShowDeleteUser] = useState(false)

    const loading = loadingOrgUser || loadingOwnOrgUser || loadingOrg || loadingOwnAppUser
    const isOrgOwner = orgUser?.userId === org?.owner.appUser?.id
    const isOwnUser = ownOrgUser?.id === orgUser?.id

    const content = (loading) ? <FullscreenLoading screenId="Org User" /> : (
        (!orgUser || !orgUserDoc) ? (
            <List>
                <ListItem>
                    <ListItemText primary="Kunne ikke finne brukeren" />
                </ListItem>
            </List>
        ) : (
            <List>
                <ListItem>
                    <Box mx="auto" mt="40px">
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <Paper
                                elevation={3}
                                style={{
                                    position: 'relative',
                                    //width: 300,
                                    //height: 300,
                                    borderRadius: 18,
                                    display: 'flex',
                                    justifyContent: 'flex-start',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                }}
                            >
                                {(!isOwnUser && !isOrgOwner && ownOrgUser?.orgRoles.map[OrgRoles.FlatEnum.OWNER]) ? (
                                    <Fab
                                        size="medium"
                                        style={{ position: 'absolute', bottom: 40, right: -16 }}
                                        onClick={() => setShowDeleteUser(true)}
                                        color="secondary"
                                        aria-label="delete"
                                    >
                                        <Delete />
                                    </Fab>
                                ) : null}
                                <Avatar
                                    style={{ width: 120, height: 120, marginTop: -40 }}
                                    alt="Org Logo"
                                    src={orgUser.avatarImage?.original.url || undefined}
                                >
                                    <Person />
                                </Avatar>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        flexDirection: 'column',
                                        marginTop: 18,
                                        marginBottom: 18,
                                    }}
                                >
                                    <InputBase
                                        value={noOrgUserNameText(orgUser.orgContactInfo?.name?.fullName)}
                                        disabled
                                        inputProps={{
                                            'aria-label': 'org name input',
                                            style: {
                                                fontSize: 26,
                                                fontWeight: 'bold',
                                                textAlign: 'center',
                                            },
                                        }}
                                    />
                                    <Typography variant="h6">
                                        {orgUser.orgContactInfo?.email?.email ? (
                                            <Link href={`mailto:${orgUser.orgContactInfo?.email?.email}`}>
                                                {orgUser.orgContactInfo?.email?.email}
                                            </Link>
                                        ) : noEmailText(orgUser.orgContactInfo?.email?.email)}
                                    </Typography>
                                    <Typography variant="h6">
                                        {orgUser.orgContactInfo?.phone?.phoneNumberString ? (
                                            <Link href={`tel:${orgUser.orgContactInfo?.phone?.phoneNumberString}`}>
                                                {orgUser.orgContactInfo?.phone?.phoneNumberString}
                                            </Link>
                                        ) : 'Ingen tlf'}
                                    </Typography>
                                    <Typography variant="subtitle2">Opprettet: {dateFormat.format(orgUserDoc.metadata.createdAt.toDate())}</Typography>
                                    <Box mt={2}>
                                        <Link component={RouterLink} to={Paths.APP_USERS + '/' + orgUser.userId}>Gå til App bruker</Link>
                                    </Box>
                                    {isOwnUser ? (
                                        <Link component={RouterLink} to={Paths.MY_ORG_USER}>Gå til min Org bruker</Link>
                                    ) : null}
                                </div>
                            </Paper>
                        </div>
                    </Box>
                </ListItem>
                {(!loading) ? <OrgUserRolePicker orgUser={orgUser} ownAppUser={ownAppUser} ownOrgUser={ownOrgUser} isOrgOwner={isOrgOwner} isOwnUser={isOwnUser} /> : null}
            </List>
        )
    )

    return (
        <PageNavigation title="Org Bruker" backHistoryButton>
            {(showDeleteUser && ownAppUser && ownOrgUser && orgUser) ? (
                <DeleteOrgUserModal
                    orgUser={orgUser}
                    ownAppUser={ownAppUser}
                    ownOrgUser={ownOrgUser}
                    close={() => setShowDeleteUser(false)}
                    isOwnUser={isOwnUser}
                    isOrgOwner={isOrgOwner}
                    redirectOnDelete
                />
            ) : null}
            {content}
        </PageNavigation>
    )
}